import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "@layout/Navbar";
import logoFull from "@assets/icons/logo_full.png";

const ORG_TYPES = [
  {
    icon: "fa-building",
    gradient: "linear-gradient(135deg,#2563EB,#7C3AED)",
    shadow: "rgba(79,70,229,0.3)",
    accent: "#2563EB",
    accentLight: "#EFF6FF",
    title: "Small & Medium Enterprises",
    desc: "Find and place resources across your network. Access the right talent at the right time without the overhead.",
    videoId: "TlifFpxYnrQ",
    detail: {
      heading1: "Gain easy access to specialists",
      text1: "Staffing needs can change quickly when a new project lands or a position needs to be filled. Perhaps you're already working on a project, and need to identify a specialist to bounce ideas off of.",
      text1b: "FirmConnector allows you to quickly and easily access the right people with the right skills for the job.",
      heading2: "Fast. Easy. And…",
      text2: "Best of all, you will ensure you are fully utilizing your own workforce before turning to outside sources.",
    },
  },
  {
    icon: "fa-briefcase",
    gradient: "linear-gradient(135deg,#F59E0B,#DC2626)",
    shadow: "rgba(245,158,11,0.3)",
    accent: "#F59E0B",
    accentLight: "#FEF3C7",
    title: "Professional Services Firms",
    desc: "Experience efficiencies at every level. Streamline your workforce management from sourcing to placement.",
    videoId: "ROfdeyQ3AFg",
    detail: {
      heading1: "Better leverage your specialists",
      text1: "Resourcing needs can change rapidly when a new project lands or a position needs to be filled. FirmConnector allows you to quickly and easily access the right people with the right skills for the job.",
      heading2: "Improve the utilization of your team",
      text2: "Until now, smaller engagements just haven't been worth the sales efforts to find and close. With FirmConnector, you can increase revenue by easily marketing your specialists directly to your clients.",
    },
  },
  {
    icon: "fa-users",
    gradient: "linear-gradient(135deg,#16A34A,#06B6D4)",
    shadow: "rgba(16,185,129,0.3)",
    accent: "#16A34A",
    accentLight: "#F0FDF4",
    title: "Staffing Firms",
    desc: "Place candidates smarter and faster. Connect your talent pool with the right opportunities instantly.",
    videoId: "X-yGkmDWY8Y",
    detail: {
      heading1: "Open up new opportunities for candidate placements",
      text1: "Your clients have more options than ever in finding and obtaining talent. Stay relevant with a fresh, new approach to showcasing your talented candidates.",
      heading2: "",
      text2: "FirmConnector helps you proactively place available candidates quickly and accurately.",
    },
  },
];

export default function HomePage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [activeOrg, setActiveOrg] = useState(0);
  const detailRef = useRef(null);

  const selectOrg = (i) => {
    setActiveOrg(i);
    setTimeout(() => {
      const top = detailRef.current?.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: "smooth" });
    }, 0);
  };

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      <Navbar />

      {/* ══ HERO ══ */}
      <section id="home" style={{
        minHeight: "100vh",
        background: "linear-gradient(150deg,#070B18 0%,#111827 45%,#160D2E 100%)",
        display: "flex", alignItems: "center",
        position: "relative", overflow: "hidden", paddingTop: 68,
      }}>
        {/* BG effects */}
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <div style={{ position: "absolute", top: "5%",  right: "0%",  width: 700, height: 700, borderRadius: "50%", background: "radial-gradient(circle,rgba(79,70,229,0.1) 0%,transparent 60%)" }} />
          <div style={{ position: "absolute", bottom: "0%", left: "0%",   width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,0.07) 0%,transparent 60%)" }} />
          <div style={{ position: "absolute", top: "50%", left: "50%",    width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle,rgba(124,58,237,0.06) 0%,transparent 60%)" }} />
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.03) 1px,transparent 1px)", backgroundSize: "32px 32px" }} />
        </div>

        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "60px 24px", position: "relative", zIndex: 1, width: "100%" }}>
          <div className="row align-items-center g-5">

            {/* Left */}
            <div className="col-lg-5">
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(79,70,229,0.12)", border: "1px solid rgba(129,140,248,0.25)", borderRadius: 99, padding: "5px 16px 5px 8px", marginBottom: 28 }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <i className="fa-solid fa-bolt" style={{ color: "#fff", fontSize: 9 }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#A5B4FC", letterSpacing: "0.07em", textTransform: "uppercase" }}>Introducing Opportunity</span>
              </div>

              <h1 style={{ fontSize: "clamp(42px,5.5vw,64px)", fontWeight: 900, color: "#fff", lineHeight: 1.08, marginBottom: 24, letterSpacing: "-0.03em" }}>
                Unlock your<br />
                <span style={{ background: "linear-gradient(90deg,#60A5FA 0%,#34D399 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  talent pipeline
                </span>
              </h1>

              <p style={{ fontSize: 17, color: "#9CA3AF", lineHeight: 1.8, marginBottom: 40, maxWidth: 440 }}>
                Gain profound insights into the professionals available within your talent network — unified, effortless, powerful.
              </p>

              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 44 }}>
                <Link to="/login"
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 28px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 15, textDecoration: "none", boxShadow: "0 8px 32px rgba(79,70,229,0.45)", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 12px 40px rgba(79,70,229,0.55)"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "0 8px 32px rgba(79,70,229,0.45)"; }}>
                  <i className="fa-solid fa-rocket" style={{ fontSize: 13 }} /> Get Started Free
                </Link>
                <a href="#org-types" onClick={e => { e.preventDefault(); document.getElementById("org-types")?.scrollIntoView({ behavior: "smooth" }); }}
                  style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 22px", borderRadius: 99, background: "rgba(255,255,255,0.06)", color: "#D1D5DB", border: "1px solid rgba(255,255,255,0.1)", fontWeight: 600, fontSize: 15, textDecoration: "none", transition: "all 0.2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.12)"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.06)"; e.currentTarget.style.color = "#D1D5DB"; }}>
                  <i className="fa-solid fa-circle-play" style={{ fontSize: 13 }} /> See How It Works
                </a>
              </div>

              {/* Divider stats */}
              <div style={{ display: "flex", gap: 0, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 28 }}>
                {[["248", "Active Resources"], ["56", "Open Positions"], ["12", "Placed Today"]].map(([v, l], i, arr) => (
                  <div key={l} style={{ paddingRight: 24, marginRight: 24, borderRight: i < arr.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                    <div style={{ fontSize: 24, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{v}</div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4, fontWeight: 500 }}>{l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — dashboard preview */}
            <div className="col-lg-7 d-none d-lg-block">
              <div style={{ position: "relative", paddingLeft: 24 }}>

                {/* Main card */}
                <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 22, padding: "24px 22px", backdropFilter: "blur(10px)" }}>

                  {/* Titlebar */}
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ display: "flex", gap: 5 }}>
                      {["#DC2626","#F59E0B","#16A34A"].map(c => <div key={c} style={{ width: 9, height: 9, borderRadius: "50%", background: c }} />)}
                    </div>
                    <div style={{ flex: 1, height: 7, background: "rgba(255,255,255,0.05)", borderRadius: 4 }} />
                    <div style={{ padding: "3px 10px", background: "rgba(79,70,229,0.2)", border: "1px solid rgba(129,140,248,0.3)", borderRadius: 6, fontSize: 10, color: "#60A5FA", fontWeight: 700, letterSpacing: "0.05em" }}>● LIVE</div>
                  </div>

                  {/* Stat cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
                    {[
                      { l: "Active Resources", v: "248", c: "#60A5FA", ic: "fa-users",        g: "linear-gradient(135deg,rgba(129,140,248,0.15),rgba(79,70,229,0.08))" },
                      { l: "Open Positions",   v: "56",  c: "#34D399", ic: "fa-briefcase",    g: "linear-gradient(135deg,rgba(52,211,153,0.15),rgba(16,185,129,0.08))" },
                      { l: "Placed Today",     v: "12",  c: "#FBBF24", ic: "fa-check-circle", g: "linear-gradient(135deg,rgba(251,191,36,0.15),rgba(245,158,11,0.08))"  },
                      { l: "Client Requests",  v: "34",  c: "#F472B6", ic: "fa-handshake",    g: "linear-gradient(135deg,rgba(244,114,182,0.15),rgba(236,72,153,0.08))"  },
                    ].map(item => (
                      <div key={item.l} style={{ background: item.g, border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "14px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
                          <div style={{ width: 28, height: 28, borderRadius: 8, background: item.c + "20", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <i className={`fa-solid ${item.ic}`} style={{ color: item.c, fontSize: 12 }} />
                          </div>
                          <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 600 }}>{item.l}</span>
                        </div>
                        <div style={{ fontSize: 30, fontWeight: 900, color: "#fff", lineHeight: 1, letterSpacing: "-0.02em" }}>{item.v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Chart */}
                  <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 14, padding: "14px 16px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
                      <span style={{ fontSize: 11, color: "#6B7280", fontWeight: 600 }}>Resource Utilization</span>
                      <span style={{ fontSize: 10, color: "#34D399", fontWeight: 700, background: "rgba(52,211,153,0.1)", padding: "2px 8px", borderRadius: 99 }}>↑ 18% this week</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 56 }}>
                      {[48,65,42,78,58,90,68].map((h, i) => (
                        <div key={i} style={{ flex: 1, height: `${h}%`, background: i === 5 ? "linear-gradient(180deg,#60A5FA,#2563EB)" : "rgba(255,255,255,0.06)", borderRadius: "4px 4px 0 0" }} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* Floating badge bottom-left */}
                <div style={{ position: "absolute", bottom: -16, left: -8, background: "#fff", borderRadius: 14, padding: "12px 18px", boxShadow: "0 8px 32px rgba(0,0,0,0.18)", display: "flex", alignItems: "center", gap: 12, minWidth: 200 }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="fa-solid fa-check" style={{ color: "#16A34A", fontSize: 17 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>12 Placed Today</div>
                    <div style={{ fontSize: 11, color: "#16A34A", fontWeight: 600 }}>↑ 18% vs yesterday</div>
                  </div>
                </div>

                {/* Floating badge top-right */}
                <div style={{ position: "absolute", top: -14, right: -16, background: "linear-gradient(135deg,#2563EB,#7C3AED)", borderRadius: 14, padding: "12px 18px", boxShadow: "0 8px 24px rgba(79,70,229,0.4)", display: "flex", alignItems: "center", gap: 10 }}>
                  <i className="fa-solid fa-shield-halved" style={{ color: "#fff", fontSize: 18 }} />
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#fff" }}>100% Secure</div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)" }}>SSL Encrypted</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ ORG TYPES ══ */}
      <section id="org-types" style={{ padding: "100px 0", background: "#F8FAFC" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 24px" }}>
          <div className="text-center mb-5">
            <span style={{ display: "inline-block", background: "#EFF6FF", color: "#2563EB", fontSize: 12, fontWeight: 700, padding: "4px 14px", borderRadius: 99, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 16, border: "1px solid #BFDBFE" }}>
              For Everyone
            </span>
            <h2 style={{ fontSize: "clamp(28px,4vw,42px)", fontWeight: 800, color: "#111827", marginBottom: 12, letterSpacing: "-0.02em" }}>
              Select your organization type
            </h2>
            <p style={{ fontSize: 16, color: "#6B7280", maxWidth: 460, margin: "0 auto" }}>
              And discover how FirmConnector can help you
            </p>
          </div>

          <div className="row g-4">
            {ORG_TYPES.map((org, i) => (
              <div key={i} className="col-12 col-md-4">
                <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 20, padding: "32px 26px", height: "100%", transition: "all 0.25s", position: "relative", overflow: "hidden" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-8px)"; e.currentTarget.style.boxShadow = `0 20px 48px ${org.shadow}`; e.currentTarget.style.borderColor = org.accent + "40"; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; e.currentTarget.style.borderColor = "#E5E7EB"; }}>

                  {/* BG accent */}
                  <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: org.accent + "08", pointerEvents: "none" }} />

                  <div style={{ width: 56, height: 56, borderRadius: 16, background: org.gradient, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 18, boxShadow: `0 6px 20px ${org.shadow}` }}>
                    <i className={`fa-solid ${org.icon}`} style={{ color: "#fff", fontSize: 22 }} />
                  </div>

                  <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", marginBottom: 12, letterSpacing: "-0.01em" }}>{org.title}</h3>
                  <p style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.7, marginBottom: 22 }}>{org.desc}</p>

                  <button onClick={() => selectOrg(i)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 700, color: org.accent, background: "none", border: "none", padding: 0, cursor: "pointer", transition: "gap 0.2s" }}
                    onMouseEnter={e => e.currentTarget.style.gap = "10px"}
                    onMouseLeave={e => e.currentTarget.style.gap = "6px"}>
                    Learn More <i className="fa-solid fa-arrow-right" style={{ fontSize: 11 }} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Learn More detail: video + text, switches based on selected org type */}
          <div ref={detailRef} style={{ marginTop: 64, paddingTop: 56, borderTop: "1px solid #E5E7EB" }}>
            <div className="row align-items-center g-5">
              <div className="col-12 col-lg-6">
                <div style={{ position: "relative", paddingTop: "56.25%", borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 48px rgba(0,0,0,0.12)" }}>
                  <iframe
                    key={ORG_TYPES[activeOrg].videoId}
                    src={`https://www.youtube.com/embed/${ORG_TYPES[activeOrg].videoId}`}
                    title={ORG_TYPES[activeOrg].title}
                    style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen />
                </div>
              </div>
              <div className="col-12 col-lg-6">
                <h3 style={{ fontSize: "clamp(22px,3vw,30px)", fontWeight: 800, color: "#111827", marginBottom: 16, letterSpacing: "-0.01em" }}>
                  {ORG_TYPES[activeOrg].detail.heading1}
                </h3>
                <p style={{ fontSize: 15, color: "#4B5563", lineHeight: 1.8, marginBottom: 18 }}>{ORG_TYPES[activeOrg].detail.text1}</p>
                {ORG_TYPES[activeOrg].detail.text1b && (
                  <p style={{ fontSize: 15, color: "#4B5563", lineHeight: 1.8, marginBottom: 18 }}>{ORG_TYPES[activeOrg].detail.text1b}</p>
                )}
                {ORG_TYPES[activeOrg].detail.heading2 && (
                  <h3 style={{ fontSize: "clamp(20px,2.6vw,26px)", fontWeight: 800, color: "#111827", margin: "28px 0 16px", letterSpacing: "-0.01em" }}>
                    {ORG_TYPES[activeOrg].detail.heading2}
                  </h3>
                )}
                <p style={{ fontSize: 15, color: "#4B5563", lineHeight: 1.8 }}>{ORG_TYPES[activeOrg].detail.text2}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHY ══ */}
      <section style={{ padding: "100px 0", background: "#fff" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
          <span style={{ display: "inline-block", background: "#F0FDF4", color: "#16A34A", fontSize: 12, fontWeight: 700, padding: "4px 14px", borderRadius: 99, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 18, border: "1px solid #A7F3D0" }}>
            Why FirmConnector
          </span>
          <h2 style={{ fontSize: "clamp(26px,4vw,38px)", fontWeight: 800, color: "#111827", marginBottom: 40, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            The right people,<br />with the right skills
          </h2>
          <div style={{ textAlign: "left" }}>
            {[
              { icon: "fa-bolt",         color: "#2563EB", bg: "#EFF6FF", text: "Staffing needs can change quickly. FirmConnector helps you identify the right specialist instantly — no back-and-forth required." },
              { icon: "fa-users",        color: "#16A34A", bg: "#F0FDF4", text: "Quickly access the right people with the right skills for the job, directly from your own talent network." },
              { icon: "fa-shield-halved",color: "#F59E0B", bg: "#FEF3C7", text: "Fully utilize your own workforce before turning to outside sources — saving time and reducing costs." },
            ].map((item, i) => (
              <div key={i} style={{ display: "flex", gap: 16, marginBottom: 20 }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: item.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: `1px solid ${item.color}20` }}>
                  <i className={`fa-solid ${item.icon}`} style={{ color: item.color, fontSize: 17 }} />
                </div>
                <p style={{ fontSize: 15, color: "#6B7280", lineHeight: 1.75, margin: "8px 0 0" }}>{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ELEVATE ══ */}
      <section style={{ padding: "100px 0", background: "linear-gradient(150deg,#070B18 0%,#111827 55%,#160D2E 100%)", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(rgba(255,255,255,0.025) 1px,transparent 1px)", backgroundSize: "32px 32px", pointerEvents: "none" }} />
        <div style={{ position: "absolute", top: "20%", right: "5%", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(79,70,229,0.1) 0%,transparent 65%)", pointerEvents: "none" }} />

        <div style={{ maxWidth: 1160, margin: "0 auto", padding: "0 24px", position: "relative", zIndex: 1 }}>
          <div className="row g-5 align-items-center">
            <div className="col-lg-5">
              <span style={{ display: "inline-block", background: "rgba(129,140,248,0.12)", color: "#60A5FA", fontSize: 12, fontWeight: 700, padding: "4px 14px", borderRadius: 99, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 18, border: "1px solid rgba(129,140,248,0.2)" }}>
                Platform
              </span>
              <h2 style={{ fontSize: "clamp(28px,4vw,44px)", fontWeight: 800, color: "#fff", marginBottom: 20, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                Elevate your<br />organization
              </h2>
              <p style={{ fontSize: 16, color: "#9CA3AF", lineHeight: 1.8, marginBottom: 14 }}>
                Experience a comprehensive and streamlined view that consolidates all your talent options into a single, intuitive interface.
              </p>
              <p style={{ fontSize: 15, color: "#6B7280", lineHeight: 1.8, marginBottom: 36 }}>
                From identifying key skillsets to assessing availability, our platform offers a holistic perspective, enabling you to make informed decisions and strategically navigate your talent pool.
              </p>
              <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 28px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 15, textDecoration: "none", boxShadow: "0 8px 28px rgba(79,70,229,0.4)", transition: "all 0.2s" }}
                onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                onMouseLeave={e => e.currentTarget.style.transform = ""}>
                Get Started Free <i className="fa-solid fa-arrow-right" style={{ fontSize: 13 }} />
              </Link>
            </div>

            <div className="col-lg-7">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  { icon: "fa-magnifying-glass", color: "#60A5FA", title: "Instant Search",     desc: "Find any skill across your entire network in seconds, not days." },
                  { icon: "fa-chart-pie",         color: "#34D399", title: "Live Availability",  desc: "Real-time utilization data so you always know who's free." },
                  { icon: "fa-network-wired",     color: "#FBBF24", title: "Unified Platform",   desc: "Staff, vendors, and partners all in one seamless interface." },
                  { icon: "fa-shield-halved",     color: "#F472B6", title: "Secure & Compliant", desc: "Enterprise-grade security with full GDPR compliance built in." },
                ].map((f, i) => (
                  <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "22px 20px", transition: "all 0.2s" }}
                    onMouseEnter={e => { e.currentTarget.style.background = "rgba(79,70,229,0.1)"; e.currentTarget.style.borderColor = "rgba(129,140,248,0.25)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                    onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.03)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; e.currentTarget.style.transform = ""; }}>
                    <div style={{ width: 46, height: 46, borderRadius: 12, background: f.color + "18", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                      <i className={`fa-solid ${f.icon}`} style={{ color: f.color, fontSize: 19 }} />
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#E5E7EB", marginBottom: 8 }}>{f.title}</div>
                    <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.65 }}>{f.desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ SEARCH CTA ══ */}
      <section style={{ padding: "100px 24px", background: "#fff" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <div style={{ width: 72, height: 72, borderRadius: 20, background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", boxShadow: "0 8px 32px rgba(79,70,229,0.35)" }}>
            <i className="fa-solid fa-magnifying-glass" style={{ color: "#fff", fontSize: 28 }} />
          </div>
          <h2 style={{ fontSize: "clamp(26px,4vw,42px)", fontWeight: 800, color: "#111827", marginBottom: 16, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            Search for skillsets across your whole network quickly and easily
          </h2>
          <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, marginBottom: 10 }}>
            Search for skillsets and availability across your company.
          </p>
          <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.8, marginBottom: 44 }}>
            Connect with the people you need across your network, quickly and easily.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 32px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 16, textDecoration: "none", boxShadow: "0 8px 28px rgba(79,70,229,0.35)", transition: "all 0.2s" }}
              onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
              onMouseLeave={e => e.currentTarget.style.transform = ""}>
              <i className="fa-solid fa-rocket" style={{ fontSize: 14 }} /> Start for Free
            </Link>
            <Link to="/contact" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "14px 26px", borderRadius: 99, background: "transparent", color: "#2563EB", border: "1.5px solid #BFDBFE", fontWeight: 700, fontSize: 15, textDecoration: "none", transition: "all 0.2s" }}
              onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══ */}
      <footer style={{ background: "#070B18", borderTop: "1px solid rgba(255,255,255,0.05)", padding: "56px 24px 28px" }}>
        <div style={{ maxWidth: 1160, margin: "0 auto" }}>
          <div className="row g-4 mb-5">
            <div className="col-12 col-md-4">
              <img src={logoFull} alt="FirmConnector" style={{ height: 30, objectFit: "contain", marginBottom: 16, filter: "brightness(0) invert(1) opacity(0.8)" }} />
              <p style={{ fontSize: 14, color: "#9CA3AF", lineHeight: 1.75, maxWidth: 300 }}>
                Connecting firms, job seekers, resource managers, and clients on one powerful platform.
              </p>
              <a href="mailto:info@firmconnector.com" style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 13, color: "#60A5FA", textDecoration: "none", marginTop: 10 }}>
                <i className="fa-solid fa-envelope" /> info@firmconnector.com
              </a>
            </div>

            <div className="col-6 col-md-2 offset-md-2">
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Company</div>
              {[["Home", "/"], ["About Us", "/about"], ["Contact Us", "/contact"]].map(([l, to]) => (
                <Link key={l} to={to} style={{ display: "block", fontSize: 14, color: "#9CA3AF", marginBottom: 10, textDecoration: "none", transition: "color 0.15s" }}
                  onMouseEnter={e => e.target.style.color = "#E5E7EB"}
                  onMouseLeave={e => e.target.style.color = "#9CA3AF"}>{l}</Link>
              ))}
            </div>

            <div className="col-6 col-md-2">
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Legal</div>
              <Link to="/privacy" style={{ display: "block", fontSize: 14, color: "#9CA3AF", marginBottom: 10, textDecoration: "none", transition: "color 0.15s" }}
                onMouseEnter={e => e.target.style.color = "#E5E7EB"}
                onMouseLeave={e => e.target.style.color = "#9CA3AF"}>Privacy Policy</Link>
            </div>

            <div className="col-12 col-md-2">
              <Link to="/login" style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 20px", borderRadius: 99, background: "linear-gradient(135deg,#2563EB,#7C3AED)", color: "#fff", fontWeight: 700, fontSize: 14, textDecoration: "none", boxShadow: "0 4px 14px rgba(79,70,229,0.35)" }}>
                <i className="fa-solid fa-right-to-bracket" style={{ fontSize: 12 }} /> Sign In
              </Link>
            </div>
          </div>

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 24, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
            <div style={{ fontSize: 13, color: "#6B7280" }}>© 2026 firmconnector.com. All Rights Reserved.</div>
            <Link to="/privacy" style={{ fontSize: 13, color: "#6B7280", textDecoration: "none" }}>Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
